<template>
  <div class="signature-component">
    <form @submit.prevent="generate" @reset.prevent="resetForm">
          <!-- Nhập Secret Key -->
          <div class="field-label">
            Secret Key <span class="text-danger">*</span>
          </div>
          <div class="secret-key-wrapper">
            <input
              type="text"
              required
              name="secretKey"
              id="secretKey"
              v-model="secretKey"
              placeholder="Khóa bí mật để ký request"
            />
          </div>

          <!-- Toggle Cấu hình nâng cao -->
          <div class="advanced-toggle" @click="showAdvanced = !showAdvanced">
            <i :class="showAdvanced ? 'fa fa-angle-down' : 'fa fa-angle-right'"></i>
            <span>Cấu hình nâng cao</span>
          </div>

          <!-- Nội dung Cấu hình nâng cao -->
          <div v-show="showAdvanced" class="advanced-section">
            <div class="field-label">JSON Object</div>
            <textarea
              name="jsonInput"
              id="jsonInput"
              v-model="jsonInput"
              placeholder='{"key": "value"}'
              rows="6"
            ></textarea>

            <div class="row mt-2">
              <div class="col-md-4">
                <div class="field-label">Hash Algorithm</div>
                <select id="algorithm" v-model="algorithm" @change="saveOptions">
                  <option value="SHA-512">SHA-512</option>
                  <option value="SHA-256">SHA-256</option>
                  <option value="SHA-384">SHA-384</option>
                  <option value="SHA-1">SHA-1</option>
                </select>
              </div>

              <div class="col-md-4">
                <div class="field-label">Separator (join values)</div>
                <input
                  type="text"
                  name="separator"
                  id="separator"
                  v-model="separator"
                  @input="saveOptions"
                  placeholder="Để trống = không có ký tự phân tách"
                />
              </div>

              <div class="col-md-4">
                <div class="field-label">Output Format</div>
                <select id="outputFormat" v-model="outputFormat" @change="saveOptions">
                  <option value="hex-lower">Hex (lowercase)</option>
                  <option value="hex-upper">Hex (UPPERCASE)</option>
                  <option value="base64">Base64</option>
                  <option value="base64url">Base64 URL</option>
                </select>
              </div>
            </div>

            <!-- Block-code hiển thị signature sinh ra -->
            <div class="signature-result-wrapper mt-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="field-label m-0">Signature (chữ ký sinh ra)</div>
                <button
                  v-if="output"
                  type="button"
                  class="btn btn-sm btn-outline-secondary btn-copy"
                  @click="copySignature"
                  title="Sao chép signature"
                >
                  <i class="fa fa-copy mr-1"></i> {{ copied ? "Đã sao chép" : "Sao chép" }}
                </button>
              </div>
              <div class="signature-block-code">
                <code v-if="output">{{ output }}</code>
                <span v-else class="text-muted font-italic">
                  (Chưa có signature - vui lòng nhập Secret Key)
                </span>
              </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
const STORAGE_KEY = "signature_options";

export default {
  name: "GenerateSignature",
  props: {
    isEmbedded: {
      type: Boolean,
      default: false
    },
    payload: {
      type: Object,
      default: () => null
    },
    merchant: {
      type: String,
      default: ""
    },
    ref_id: {
      type: [String, Number],
      default: ""
    },
    description: {
      type: String,
      default: ""
    },
    paygate_id: {
      type: [String, Number],
      default: ""
    },
    data: {
      type: [Object, Array],
      default: () => null
    }
  },
  data() {
    return {
      jsonInput: "",
      secretKey: "",
      showAdvanced: false,
      algorithm: "SHA-512",
      separator: "|",
      outputFormat: "hex-lower",
      output: "",
      copied: false
    };
  },
  computed: {
    targetPayload() {
      if (this.payload && typeof this.payload === "object") {
        return this.payload;
      }
      if (this.merchant || this.ref_id || this.paygate_id) {
        const obj = {};
        if (this.merchant !== undefined) obj.merchant = this.merchant;
        if (this.ref_id !== undefined) obj.ref_id = this.ref_id;
        if (this.description !== undefined) obj.description = this.description;
        if (this.paygate_id !== undefined) obj.paygate_id = this.paygate_id;
        if (this.data !== undefined && this.data !== null) obj.data = this.data;
        return obj;
      }
      return null;
    }
  },
  watch: {
    targetPayload: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          try {
            const clean = JSON.parse(JSON.stringify(val));
            this.jsonInput = JSON.stringify(clean, null, 2);
          } catch (e) {
            console.error("Error stringifying payload:", e);
          }
        }
      }
    },
    secretKey() {
      this.autoComputeSignature();
    },
    jsonInput() {
      this.autoComputeSignature();
    },
    algorithm() {
      this.autoComputeSignature();
    },
    separator() {
      this.autoComputeSignature();
    },
    outputFormat() {
      this.autoComputeSignature();
    }
  },
  mounted() {
    this.loadOptions();
    this.autoComputeSignature();
  },
  methods: {
    saveOptions() {
      const options = {
        algorithm: this.algorithm,
        separator: this.separator,
        outputFormat: this.outputFormat
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
    },
    loadOptions() {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      try {
        const options = JSON.parse(raw);
        if (options.algorithm) this.algorithm = options.algorithm;
        if (options.separator !== undefined) this.separator = options.separator;
        if (options.outputFormat) this.outputFormat = options.outputFormat;
      } catch (e) {
        /* ignore */
      }
    },
    resetForm() {
      if (this.targetPayload) {
        try {
          const clean = JSON.parse(JSON.stringify(this.targetPayload));
          this.jsonInput = JSON.stringify(clean, null, 2);
        } catch (e) {
          this.jsonInput = "";
        }
      } else {
        this.jsonInput = "";
      }
      this.secretKey = "";
      this.output = "";
    },
    arrayBufferToHex(buffer) {
      return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    },
    arrayBufferToBase64(buffer) {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    },
    arrayBufferToBase64Url(buffer) {
      return this.arrayBufferToBase64(buffer)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
    },
    formatOutput(buffer, format) {
      switch (format) {
        case "hex-lower":
          return this.arrayBufferToHex(buffer);
        case "hex-upper":
          return this.arrayBufferToHex(buffer).toUpperCase();
        case "base64":
          return this.arrayBufferToBase64(buffer);
        case "base64url":
          return this.arrayBufferToBase64Url(buffer);
        default:
          return this.arrayBufferToHex(buffer);
      }
    },
    flattenObject(obj, prefix, result) {
      if (obj === null) {
        result[prefix] = null;
        return;
      }
      if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
          const key = prefix ? prefix + "." + i : "" + i;
          this.flattenObject(obj[i], key, result);
        }
        return;
      }
      if (typeof obj === "object") {
        for (const k of Object.keys(obj)) {
          const key = prefix ? prefix + "." + k : k;
          this.flattenObject(obj[k], key, result);
        }
        return;
      }
      // primitive value
      result[prefix] = obj;
    },
    valueToString(val) {
      if (val === null) return "";
      if (val === undefined) return undefined;
      if (typeof val === "boolean") return val ? "true" : "false";
      if (typeof val === "string") {
        return val;
      }
      if (typeof val === "number") {
        if (Number.isInteger(val)) return val.toString();
        return parseFloat(val.toString()).toString();
      }
      return String(val);
    },
    copySignature() {
      if (!this.output) return;
      const done = () => {
        this.copied = true;
        if (window.toastr) window.toastr.success("Đã sao chép signature");
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      };

      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.output).then(done).catch(() => {
          this.fallbackCopy(this.output, done);
        });
      } else {
        this.fallbackCopy(this.output, done);
      }
    },
    fallbackCopy(text, callback) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        if (callback) callback();
      } catch (err) {
        console.error("Fallback copy error:", err);
      }
      document.body.removeChild(textarea);
    },
    autoComputeSignature() {
      if (this._computeTimer) clearTimeout(this._computeTimer);
      this._computeTimer = setTimeout(async () => {
        if (!this.secretKey || !this.jsonInput.trim()) {
          this.output = "";
          return;
        }
        let obj;
        try {
          obj = JSON.parse(this.jsonInput);
        } catch (e) {
          return;
        }
        try {
          const flatMap = {};
          this.flattenObject(obj, "", flatMap);
          const sortedKeys = Object.keys(flatMap).sort();
          const values = sortedKeys.map((k) => this.valueToString(flatMap[k]));
          const message = values.join(this.separator);

          const encoder = new TextEncoder();
          const keyData = encoder.encode(this.secretKey);
          const messageData = encoder.encode(message);

          const cryptoKey = await window.crypto.subtle.importKey(
            "raw",
            keyData,
            { name: "HMAC", hash: this.algorithm },
            false,
            ["sign"]
          );

          const signatureBuffer = await window.crypto.subtle.sign(
            "HMAC",
            cryptoKey,
            messageData
          );
          this.output = this.formatOutput(signatureBuffer, this.outputFormat);
        } catch (e) {
          // ignore in background auto-compute
        }
      }, 150);
    },
    // Hàm gọi từ ngoài component (ví dụ MerchantTest gọi getSignature())
    async getSignature() {
      return await this.generate();
    },
    async generate() {
      if (!this.secretKey) {
        const msg = "Vui lòng nhập Secret Key.";
        if (window.toastr) window.toastr.warning(msg);
        else alert(msg);
        return null;
      }

      if (!this.jsonInput.trim()) {
        const msg = "Vui lòng nhập JSON Object.";
        if (window.toastr) window.toastr.warning(msg);
        else alert(msg);
        return null;
      }

      // Validate JSON
      let obj;
      try {
        obj = JSON.parse(this.jsonInput);
      } catch (e) {
        const msg = "Invalid JSON format: " + e.message;
        if (window.toastr) window.toastr.error(msg);
        else alert(msg);
        return null;
      }

      try {
        // Flatten object to key-value pairs
        const flatMap = {};
        this.flattenObject(obj, "", flatMap);

        // Sort keys ascending (case-sensitive)
        const sortedKeys = Object.keys(flatMap).sort();

        // Convert values to string and join with separator
        const values = sortedKeys.map((k) => this.valueToString(flatMap[k]));
        const message = values.join(this.separator);

        const encoder = new TextEncoder();
        const keyData = encoder.encode(this.secretKey);
        const messageData = encoder.encode(message);

        const cryptoKey = await window.crypto.subtle.importKey(
          "raw",
          keyData,
          { name: "HMAC", hash: this.algorithm },
          false,
          ["sign"]
        );

        const signatureBuffer = await window.crypto.subtle.sign(
          "HMAC",
          cryptoKey,
          messageData
        );
        const result = this.formatOutput(signatureBuffer, this.outputFormat);

        this.output = result;

        this.$emit("signature-generated", {
          signature: result,
          payload: obj,
          algorithm: this.algorithm,
          separator: this.separator,
          outputFormat: this.outputFormat
        });
        this.$emit("input", result);

        return result;
      } catch (e) {
        this.output = "Error: " + e.message;
        console.error(e);
        if (window.toastr) window.toastr.error("Lỗi sinh signature: " + e.message);
        return null;
      }
    }
  }
};
</script>

<style scoped>
.signature-component {
  width: 100%;
}
.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #5c6873;
  margin-top: 10px;
  margin-bottom: 6px;
}
textarea,
input[type="text"],
input[type="password"] {
  width: 100%;
  display: block;
  padding: 8px 12px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #c8ced3;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
  background-color: #fff;
}
textarea:focus,
input[type="text"]:focus,
input[type="password"]:focus,
select:focus {
  border-color: #20a8d8;
}
.secret-key-wrapper {
  width: 100%;
}
.advanced-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 12px;
  color: #20a8d8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
}
.advanced-toggle:hover {
  color: #1b8eb7;
  text-decoration: underline;
}
.advanced-section {
  padding: 16px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  margin-bottom: 16px;
}
#jsonInput {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
}
select {
  width: 100%;
  display: block;
  margin: 5px 0;
  padding: 8px 12px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #c8ced3;
  background-color: #fff;
  box-sizing: border-box;
}
.signature-block-code {
  padding: 10px 14px;
  background-color: #2b3035;
  color: #98c379;
  border: 1px solid #3a4149;
  border-radius: 4px;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
  white-space: pre-wrap;
  user-select: all;
  min-height: 40px;
  display: flex;
  align-items: center;
}
.signature-block-code code {
  color: #98c379;
  background: transparent;
  padding: 0;
  font-size: 13px;
}
.btn-copy {
  padding: 2px 10px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 4px;
}
</style>
